import React, { useEffect, useRef } from "react";
import { FiPlay } from "react-icons/fi";
import { FaGooglePlay } from "react-icons/fa";
import Section from "./Section";

const project = {
  title: "Lithe",
  kind: "Mobile app for coding on your phone",
  description:
    "Clone a GitHub repo, edit code in a native editor, ask an AI assistant to make changes, and commit without leaving your phone.",
  stack: ["React Native", "Expo", "libgit2", "OpenRouter"],
  tour: "/lithe/video.html",
  store: "https://play.google.com/store/apps/details?id=com.litheapp.app",
};

// Plays the real app recording, muted and looping. Holds on the poster
// frame for people who prefer reduced motion.
function PhoneClip() {
  const video = useRef(null);

  useEffect(() => {
    const el = video.current;
    if (!el) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => (reduce.matches ? el.pause() : el.play().catch(() => {}));
    sync();
    reduce.addEventListener("change", sync);
    return () => reduce.removeEventListener("change", sync);
  }, []);

  return (
    <div className="w-[168px] sm:w-[180px] shrink-0 rounded-[30px] sm:rounded-[36px] bg-black p-[6px] sm:p-[7px] ring-1 ring-white/10 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.04),0_24px_60px_-24px_rgba(143,209,106,0.35)]">
      <video
        ref={video}
        className="block w-full aspect-[9/20] rounded-[24px] sm:rounded-[29px] object-cover"
        muted
        loop
        playsInline
        preload="metadata"
        poster="/lithe/lithe-card.jpg"
        aria-label="Screen recording of the Lithe app: AI chat, code editor and Git"
      >
        <source src="/lithe/lithe-card.webm" type="video/webm" />
        <source src="/lithe/lithe-card.mp4" type="video/mp4" />
      </video>
    </div>
  );
}

function Projects() {
  return (
    <Section id="work" label="Work" wide>
      <article className="overflow-hidden rounded-xl border border-line bg-surface p-5 md:px-10 md:pt-10 sm:pb-0 grid sm:grid-cols-[minmax(0,1fr)_auto] gap-x-10 gap-y-6">
        <div className="sm:self-start sm:pb-10">
          <h3 className="text-3xl md:text-[34px] font-semibold text-fg tracking-[-0.02em]">
            {project.title}
          </h3>
          <p className="mt-2 text-base sm:text-lg leading-[1.4] text-fg/90 font-medium">
            {project.kind}
          </p>
          <p className="mt-4 text-[15px] leading-[1.6] sm:text-[17px] sm:leading-[1.65] text-body max-w-[52ch] [text-wrap:pretty]">
            {project.description}
          </p>
          <ul className="mt-4 flex flex-wrap gap-1.5" aria-label="Built with">
            {project.stack.map((tech) => (
              <li
                key={tech}
                className="inline-flex items-center h-7 px-2.5 rounded-md border font-mono text-xs border-line text-body"
              >
                {tech}
              </li>
            ))}
          </ul>
          <div className="mt-6 grid grid-cols-2 sm:flex sm:flex-wrap sm:items-center gap-3">
            <a
              href={project.tour}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 h-11 px-3 sm:px-5 rounded-md bg-leaf/10 border border-leaf/40 text-leaf text-[13px] sm:text-sm font-semibold hover:bg-leaf/15 transition-colors"
            >
              <FiPlay aria-hidden="true" />
              Watch the tour
            </a>
            <a
              href={project.store}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 h-11 px-3 sm:px-5 rounded-md border border-line text-fg text-[13px] sm:text-sm font-medium hover:border-leaf transition-colors"
            >
              <FaGooglePlay className="text-leaf" aria-hidden="true" />
              Google Play
            </a>
          </div>
        </div>

        {/* The phone rises from the card's bottom edge and is cropped by it. */}
        <div className="justify-self-center self-end h-[210px] sm:h-[340px] overflow-hidden -mb-5 sm:mb-0">
          <PhoneClip />
        </div>
      </article>
    </Section>
  );
}

export default Projects;
